import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, selectSignUpState, resetSignUpState } from "../../../redux/WithcookieSlice";
import axios from "axios";
import { showToast } from "../../common/Toast";
import { useTranslation } from "../../../redux/TranslationHelper";
import { SignupFormData, VarFieldConfig } from "./Signup.types";
import { ComponentData, ComponentRenderMode } from '../../../types/component-skin';

export const useSignupLogic = (componentData: ComponentData, mode: ComponentRenderMode) => {
  // Extract props from componentData for compatibility
  const props = { ...componentData.props, ...componentData.componentProps, ...componentData };
  const dispatch = useDispatch() as any;
  const { t } = useTranslation();
  
  // Redux state
  const { loading, signUpSuccess, signUpError, validationErrors: reduxValidationErrors } = useSelector(selectSignUpState);
  const withcookieData = useSelector((state: any) => state.withcookie.withcookieData);
  
  // Extract data from props
  const referralCodeFromProps = props.pathVariables?.referralCode || props.content?.referralCode || props.data?.referralCode;
  
  // Theme data
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#007bff";
  const secondaryColor = colorSet?.secondary || "#6c757d";
  
  // Company data
  const companyId = withcookieData?.skin?.companyId;
  
  // Local state
  const [checkCurrentData, setCheckCurrentData] = useState<any>(null);
  const [directReferralCode, setDirectReferralCode] = useState("");
  const [formData, setFormData] = useState<SignupFormData>({
    user_id: "",
    password: "",
    phone: "",
    name: "",
    referral_code: "",
    email: "",
    birthday: "",
    address: "",
    upperer_code: "",
    center_code: "",
    left_or_right: "0",
    var01: "",
    var02: "",
    var03: "",
    var04: "",
    var05: "",
    var06: "",
    var07: "",
    var08: "",
    var09: "",
    var10: "",
    referral_code_from_url: false,
    trxWithdrawAddress: "",
    bscWithdrawAddress: ""
  });
  
  const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({});
  const [policies, setPolicies] = useState<any>(null);
  const [isSpecifiedLegPolicy, setIsSpecifiedLegPolicy] = useState(false);
  const [isCheckCurrentLoading, setIsCheckCurrentLoading] = useState(false);
  const [isPoliciesLoading, setIsPoliciesLoading] = useState(false);
  
  // Refs
  const formDataInitialized = useRef(false);
  const checkCurrentCallRef = useRef(false);
  const policiesCallRef = useRef(false);
  
  // Merge validation errors
  const validationErrors = useMemo(() => ({ 
    ...localValidationErrors, 
    ...(reduxValidationErrors || {}) 
  }), [localValidationErrors, reduxValidationErrors]);
  
  // Field validation
  const validateField = useCallback((id: string, value: string) => {
    let errors = {...localValidationErrors};
    
    switch(id) {
      case 'user_id':
        if (!value.trim()) {
          errors.user_id = t('ID를 입력해주세요');
        } else if (value.length < 2 || value.length > 20) {
          errors.user_id = t('ID는 2~20자로 입력해주세요');
        } else {
          delete errors.user_id;
        }
        break;
        
      case 'password':
        if (!value) {
          errors.password = t('비밀번호를 입력해주세요');
        } else {
          delete errors.password;
        }
        break;
        
      case 'phone':
        if (!value) {
          errors.phone = t('핸드폰 번호를 입력해주세요');
        } else {
          delete errors.phone;
        }
        break;
        
      case 'name':
        if (!value.trim()) {
          errors.name = t('이름을 입력해주세요');
        } else if (!/^[가-힣a-zA-Z0-9_-]+$/.test(value)) {
          errors.name = t('이름은 한글, 영문, 숫자, _, - 만 사용 가능합니다');
        } else {
          delete errors.name;
        }
        break;
        
      case 'email':
        if (value && !/\S+@\S+\.\S+/.test(value)) {
          errors.email = t('유효한 이메일 형식이 아닙니다');
        } else {
          delete errors.email;
        }
        break;
        
      default:
        delete errors[id];
        break;
    }
    
    setLocalValidationErrors(errors);
  }, [localValidationErrors, t]);
  
  // Form validation
  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    const { user_id, password, phone, name, email } = formData;
    
    // Basic fields validation
    if (!user_id.trim()) {
      errors.user_id = t('ID를 입력해주세요');
    } else if (user_id.length < 2 || user_id.length > 20) {
      errors.user_id = t('ID는 2~20자로 입력해주세요');
    }
    
    if (!password) {
      errors.password = t('비밀번호를 입력해주세요');
    }
    
    if (!phone) {
      errors.phone = t('핸드폰 번호를 입력해주세요');
    }
    
    if (!name.trim()) {
      errors.name = t('이름을 입력해주세요');
    } else if (!/^[가-힣a-zA-Z0-9_-]+$/.test(name)) {
      errors.name = t('이름은 한글, 영문, 숫자, _, - 만 사용 가능합니다');
    }
    
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('유효한 이메일 형식이 아닙니다');
    }
    
    // Custom fields validation
    const varFields = props.varFields || {};
    Object.entries(varFields).forEach(([fieldKey, fieldConfig]) => {
      const fieldValue = formData[fieldKey as keyof SignupFormData];
      const config = fieldConfig as VarFieldConfig;
      
      if (config.show && config.required) {
        if (config.type === 'document' && !fieldValue) {
          errors[fieldKey] = `${config.label}을 동의하셔야 합니다.`;
        } else if (config.type === 'account') {
          const [bankCode, accountNumber] = (fieldValue || '').toString().split('|');
          if (!bankCode || !accountNumber) {
            errors[fieldKey] = `${config.label}을 올바르게 입력해주세요.`;
          }
        } else if (config.type === 'ssn') {
          const [ssnFront, ssnBack] = (fieldValue || '').toString().split('-');
          if (!ssnFront || ssnFront.length !== 6 || !ssnBack || ssnBack.length !== 7) {
            errors[fieldKey] = `${config.label}을 올바른 형식으로 입력해주세요.`;
          }
        } else if (!fieldValue) {
          errors[fieldKey] = `${config.label}을 입력해주세요.`;
        }
      }
    });
    
    setLocalValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, props.varFields, t]);
  
  // Input change handler
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    
    // Prevent changing referral_code if it's from URL
    if (id === 'referral_code' && formData.referral_code_from_url) {
      return;
    }
    
    setFormData(prevData => ({
      ...prevData,
      [id]: (e.target as HTMLInputElement).type === 'radio' ? value : value
    }));

    validateField(id, value);
  }, [formData.referral_code_from_url, validateField]);
  
  // Radio button handler
  const handleRadioChange = useCallback((fieldName: string, value: string) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: value
    }));
  }, []);
  
  // Checkbox handler
  const handleCheckboxChange = useCallback((fieldName: string, label: string, isChecked: boolean) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: isChecked ? `${label} 동의함` : ''
    }));
  }, []);
  
  // Form submit handler
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Prepare API data
    const apiData: any = {
      user_id: formData.user_id,
      password: formData.password,
      name: formData.name,
      phone: formData.phone,
      referral_code: formData.referral_code,
      email: formData.email,
      birthday: formData.birthday,
      address: formData.address,
      var01: formData.var01,
      var02: formData.var02,
      var03: formData.var03,
      var04: formData.var04,
      var05: formData.var05,
      var06: formData.var06,
      var07: formData.var07,
      var08: formData.var08,
      var09: formData.var09,
      var10: formData.var10,
      center_code: formData.center_code
    };
    
    // Add SPECIFIED policy fields
    if (isSpecifiedLegPolicy) {
      apiData.upperer_code = formData.upperer_code;
      apiData.left_or_right = formData.left_or_right;
    }
    
    // Add wallet addresses for specific companies
    if (companyId === 24) {
      apiData.trxWithdrawAddress = formData.trxWithdrawAddress;
      apiData.bscWithdrawAddress = formData.bscWithdrawAddress;
    }
    
    try {
      await dispatch(registerUser(apiData)).unwrap();
      showToast(t("회원가입이 완료되었습니다!"), "success");
    } catch (error: any) {
      const errorMessage = error.message || t("회원가입에 실패했습니다.");
      showToast(errorMessage, "error");
      console.error(t('[회원가입 에러]'), error);
    }
  }, [formData, validateForm, isSpecifiedLegPolicy, companyId, dispatch, t]);
  
  // Blur handler
  const handleBlur = useCallback((field: string) => {
    const value = formData[field as keyof SignupFormData] as string;
    validateField(field, value || '');
  }, [formData, validateField]);
  
  // Check current API
  useEffect(() => {
    if (isCheckCurrentLoading || checkCurrentData || checkCurrentCallRef.current) return;
    
    const fetchCheckCurrent = async () => {
      checkCurrentCallRef.current = true;
      setIsCheckCurrentLoading(true);
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const response = await axios.get(`${apiBaseUrl}/api/check_current`, {
          withCredentials: true
        });
        
        setCheckCurrentData(response.data);
        
        if ((response.data as any)?.skin?.referralCode) {
          setDirectReferralCode((response.data as any).skin.referralCode);
        }
      } catch (error) {
        console.error(t('check_current API 호출 오류:'), error);
      } finally {
        setIsCheckCurrentLoading(false);
        checkCurrentCallRef.current = false;
      }
    };
    
    fetchCheckCurrent();
  }, [t, isCheckCurrentLoading, checkCurrentData]);
  
  // Load policies
  useEffect(() => {
    if (isPoliciesLoading || policies || policiesCallRef.current) return;
    
    const fetchPolicies = async () => {
      policiesCallRef.current = true;
      setIsPoliciesLoading(true);
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const response = await axios.get(`${apiBaseUrl}/api/policies/all`);
        setPolicies(response.data);
        
        const responseData = response.data as any;
        if (responseData && responseData.legPolicies && responseData.legPolicies.length > 0) {
          const hasSpecifiedLegPolicy = responseData.legPolicies.some((policy: any) => policy.type === 'SPECIFIED');
          setIsSpecifiedLegPolicy(hasSpecifiedLegPolicy);
        }
      } catch (error) {
        console.error(t('정책 로드 중 오류 발생:'), error);
      } finally {
        setIsPoliciesLoading(false);
        policiesCallRef.current = false;
      }
    };
    
    fetchPolicies();
  }, [t, isPoliciesLoading, policies]);
  
  // Set referral code
  useEffect(() => {
    if (directReferralCode && directReferralCode.trim() !== "") {
      setFormData(prevData => ({
        ...prevData,
        referral_code: directReferralCode,
        referral_code_from_url: true
      }));
    } else if (referralCodeFromProps) {
      setFormData(prevData => ({
        ...prevData,
        referral_code: referralCodeFromProps,
        referral_code_from_url: true
      }));
    } else {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      if (pathSegments.length >= 2 && pathSegments[0] === 'signup') {
        const codeFromUrl = pathSegments[1];
        setFormData(prevData => ({
          ...prevData,
          referral_code: codeFromUrl,
          referral_code_from_url: true
        }));
      }
    }
  }, [referralCodeFromProps, directReferralCode]);
  
  // Initialize var fields
  useEffect(() => {
    if (!formDataInitialized.current) {
      const initialFieldValues = {
        var01: '',
        var02: '',
        var03: '',
        var04: '',
        var05: '',
        var06: '',
        var07: '',
        var08: '',
        var09: '',
        var10: ''
      };
      
      setFormData(prevData => ({
        ...prevData,
        ...initialFieldValues
      }));
      
      formDataInitialized.current = true;
    }
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetSignUpState());
    };
  }, [dispatch]);
  
  // Redirect on success
  useEffect(() => {
    if (signUpSuccess) {
      const redirectTimer = setTimeout(() => {
        window.location.href = '/';
      }, 3000);
      
      return () => clearTimeout(redirectTimer);
    }
  }, [signUpSuccess]);
  
  return {
    // Data  
    formData,
    validationErrors,
    loading,
    signUpSuccess,
    signUpError,
    policies,
    isSpecifiedLegPolicy,
    companyId,
    theme: {
      primaryColor,
      secondaryColor
    },
    basicFields: props.basicFields || {
      userId: true,
      password: true,
      name: true,
      phone: true,
      email: true,
      birthday: true,
      address: true,
      referralCode: true
    },
    varFields: props.varFields || {},
    // Actions at top level for ComponentSkinWrapper
    actions: {
      handleChange,
      handleSubmit,
      handleBlur,
      handleRadioChange,
      handleCheckboxChange,
      validateField,
      validateForm
    }
  };
};